const USER_KEY = `${process.env.VUE_APP_ACCESS_TOKEN_NAME}User`;
// sessionStorage survives the reload, so the reloaded tab can say why it refreshed
const NOTICE_KEY = 'sessionChangedInAnotherTab';
const RELOADED_AT_KEY = 'sessionChangeReloadedAt';
// A tab stuck on a V2 login URL logs in again on every reload; cap it at one reload per window
const RELOAD_COOLDOWN_MS = 10000;

const userIdOf = (user) => (user && (user.id || user._id)) || null;

const storedUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY));
  } catch (error) {
    return null;
  }
};

/**
 * Whether this tab is acting as a different user than the one now logged in.
 * The login lives in localStorage, shared by every tab, while each tab keeps
 * its own selected clinic, so a stale tab would otherwise send its clinic with
 * another tab's login (COR-8041). A cleared login (mid-switch or logged out)
 * is not a change yet; the new user's write follows.
 *
 * This only narrows the window: a request can still leave in the moment
 * before the reload, so the server's clinic check is what actually refuses it.
 */
export const isActingAsStaleUser = (activeUser) => {
  const storedId = userIdOf(storedUser());
  const activeId = userIdOf(activeUser);

  return Boolean(storedId && activeId && String(storedId) !== String(activeId));
};

/** Whether a `storage` event from another tab changed the logged-in user away from `activeUser`. */
export const userChangedInAnotherTab = (event, activeUser) => {
  if (event.storageArea !== localStorage) return false;
  if (event.key !== null && event.key !== USER_KEY) return false;

  return isActingAsStaleUser(activeUser);
};

/** Reloads into the current login and leaves a notice for after the reload, at most once per cooldown. */
export const reloadForSessionChange = () => {
  const lastReloadAt = Number(sessionStorage.getItem(RELOADED_AT_KEY));
  if (lastReloadAt && Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) return;

  sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
  sessionStorage.setItem(NOTICE_KEY, 'true');
  window.location.reload();
};

/** Whether this tab just reloaded for a login in another tab; true only once. */
export const takeSessionChangedNotice = () => {
  if (sessionStorage.getItem(NOTICE_KEY) === null) return false;

  sessionStorage.removeItem(NOTICE_KEY);
  return true;
};
